import { visit } from 'unist-util-visit';
import type { Components } from 'react-markdown';
import { Callout } from '@/components/markdown/Callout';
import { Columns, Col } from '@/components/markdown/Columns';
import { Cards, Card } from '@/components/markdown/Cards';
import { createElement } from 'react';

// remark plugin that maps Carlorbiz custom directives onto rehype-friendly
// data-attributes. Runs after remark-directive in the plugin chain.
//
// Vocabulary:
//   :::callout{type=info|tip|warning|takeaway}
//   body
//   :::
//
//   :::columns
//   left body
//
//   ||
//
//   right body
//   :::
//
//   ::::cards{cols=2|3|4}
//   :::card{title="..."}
//   body
//   :::
//   :::card{title="..."}
//   body
//   :::
//   ::::
//
// Authoring rules the importer must enforce:
//   - The `||` column separator must sit on its own line surrounded by
//     blank lines, otherwise remark parses it as inline text.
//   - Nested directives need MORE colons on the outer block. The cards
//     pattern shipped here uses 4 colons outer + 3 colons inner. If
//     callouts/columns ever nest inside cards or each other, bump the
//     outer count further.
// Standard markdown (blockquotes, hr, headings, lists, links, images,
// emphasis, code) passes through untouched.

// remark plugin that turns literal <br> HTML nodes into real mdast `break`
// nodes. The Gamma → markdown importer emits <br> inside table cells (GFM
// tables can't hold real line breaks), but react-markdown is run without
// rehype-raw, so raw HTML would otherwise render as the visible literal text
// "<br>". Converting to a `break` node lets the line break render natively
// without opening the door to arbitrary raw HTML. Only matches a bare <br>/
// <br/> tag — nothing else is touched.
export function remarkHtmlBreaks() {
  return (tree: unknown) => {
    visit(tree as any, 'html', (node: any) => {
      if (/^<br\s*\/?>$/i.test(String(node.value || '').trim())) {
        node.type = 'break';
        delete node.value;
      }
    });
  };
}

export function remarkVocabulary() {
  return (tree: unknown) => {
    visit(tree as any, 'containerDirective', (node: any) => {
      if (node.name !== 'columns') return;
      const children = node.children || [];
      const splitIdx = children.findIndex(
        (c: any) =>
          c.type === 'paragraph' &&
          c.children?.length === 1 &&
          c.children[0].type === 'text' &&
          c.children[0].value.trim() === '||',
      );
      if (splitIdx === -1) return;
      const wrap = (kids: any[]) => ({
        type: 'containerDirective',
        name: '__col',
        attributes: {},
        children: kids,
      });
      node.children = [wrap(children.slice(0, splitIdx)), wrap(children.slice(splitIdx + 1))];
    });

    visit(tree as any, (node: any) => {
      if (
        node.type !== 'containerDirective' &&
        node.type !== 'leafDirective' &&
        node.type !== 'textDirective'
      ) {
        return;
      }
      const data = node.data || (node.data = {});
      const attrs = node.attributes || {};
      switch (node.name) {
        case 'callout':
          data.hName = 'div';
          data.hProperties = { 'data-vocab': 'callout', 'data-type': attrs.type || 'info' };
          break;
        case 'columns':
          data.hName = 'div';
          data.hProperties = { 'data-vocab': 'columns' };
          break;
        case '__col':
          data.hName = 'div';
          data.hProperties = { 'data-vocab': 'col' };
          break;
        case 'cards':
          data.hName = 'div';
          data.hProperties = { 'data-vocab': 'cards', 'data-cols': attrs.cols || '3' };
          break;
        case 'card':
          data.hName = 'div';
          data.hProperties = { 'data-vocab': 'card', 'data-title': attrs.title || '' };
          break;
      }
    });
  };
}

export const vocabularyComponents: Components = {
  div: ({ children, ...props }: any) => {
    const vocab = props['data-vocab'];
    switch (vocab) {
      case 'callout':
        return createElement(Callout, { type: props['data-type'] }, children);
      case 'columns':
        return createElement(Columns, null, children);
      case 'col':
        return createElement(Col, null, children);
      case 'cards': {
        const cols = parseInt(props['data-cols'], 10);
        return createElement(Cards, { cols: Number.isFinite(cols) ? cols : 3 }, children);
      }
      case 'card':
        return createElement(Card, { title: props['data-title'] }, children);
      default:
        return createElement('div', props, children);
    }
  },
};
